import { useState } from "react";
import { buyerRequests, buyerCategories } from "@/mocks/buyers";

const categoryColors: Record<string, string> = {
  "Vehicles & Transport": "bg-blue-50 text-blue-700",
  "Construction Materials": "bg-orange-50 text-orange-700",
  "Furniture & Home": "bg-amber-50 text-amber-700",
  "Electronics": "bg-violet-50 text-violet-700",
  "Apparel & Textiles": "bg-pink-50 text-pink-700",
  "Food & Agriculture": "bg-green-50 text-green-700",
  "Health & Beauty": "bg-rose-50 text-rose-700",
  "Machinery & Equipment": "bg-stone-100 text-stone-700",
  "Solar & Energy": "bg-yellow-50 text-yellow-700",
};

function MemberLockOverlay({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-b-xl z-10">
      <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full mb-2">
        <i className="ri-lock-fill text-amber-500 text-lg"></i>
      </div>
      <p className="text-xs font-bold text-gray-800 mb-0.5">Paid Members Only</p>
      <p className="text-xs text-stone-500 mb-3 text-center px-4">Upgrade to view buyer contact details</p>
      <button
        onClick={onUpgrade}
        className="text-xs font-bold px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        Upgrade Now
      </button>
    </div>
  );
}

function UpgradeModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative">
          <img
            src="https://public.readdy.ai/ai/img_res/104960139c1092a5cca607fba08d8d8c.jpg"
            alt="Upgrade"
            className="w-full h-32 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/70 to-amber-600/50 flex items-center px-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-vip-crown-fill text-amber-300 text-xl"></i>
                <span className="text-white font-bold text-lg">Crown Paid Membership</span>
              </div>
              <p className="text-white/80 text-xs">Unlock all buyer contacts &amp; premium features</p>
            </div>
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-black/30 rounded-full text-white cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-3 mb-6">
            {[
              { icon: "ri-contacts-line", text: "View full buyer contact details (WhatsApp & Email)" },
              { icon: "ri-global-line", text: "Access all 20+ buyer requests from 30+ countries" },
              { icon: "ri-store-2-line", text: "List your factory products with priority placement" },
              { icon: "ri-notification-3-line", text: "Get instant alerts for new buyer requests in your category" },
              { icon: "ri-shield-check-line", text: "Verified seller badge on your profile" },
              { icon: "ri-bar-chart-line", text: "Analytics dashboard for your listings" },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-amber-600 text-xs`}></i>
                </div>
                <p className="text-sm text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Plans */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {[
              { label: "Basic Member", price: "$5", period: "/mo", popular: false, icon: "ri-shield-line", desc: "Post jobs & ads, view buyer contacts" },
              { label: "Gold Member", price: "$18", period: "/mo", popular: true, icon: "ri-vip-crown-fill", desc: "Unlimited posts, priority placement & more" },
            ].map((plan) => (
              <div
                key={plan.label}
                className={`relative rounded-xl p-3 text-center cursor-pointer border-2 transition-all ${
                  plan.popular
                    ? "border-amber-500 bg-amber-50"
                    : "border-stone-200 hover:border-amber-300"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                )}
                <i className={`${plan.icon} ${plan.popular ? "text-amber-500" : "text-stone-500"} text-lg mb-1 block`}></i>
                <p className="text-xs font-semibold text-gray-800 mb-0.5">{plan.label}</p>
                <p className="font-bold text-gray-900 text-xl leading-none">{plan.price}<span className="text-xs font-normal text-stone-400">{plan.period}</span></p>
                <p className="text-xs text-stone-400 mt-1 leading-tight">{plan.desc}</p>
              </div>
            ))}
          </div>

          <a
            href="/membership"
            className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-vip-crown-fill"></i>
            View Plans &amp; Subscribe
          </a>
          <div className="flex items-center justify-center gap-3 mt-3">
            <i className="ri-bank-card-line text-stone-400 text-base"></i>
            <i className="ri-mastercard-line text-stone-400 text-base"></i>
            <i className="ri-paypal-line text-stone-400 text-base"></i>
            <span className="text-xs text-stone-400">Credit · Debit · PayPal</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BuyerRequestsSection() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const filtered = activeCategory === "All"
    ? buyerRequests
    : buyerRequests.filter((b) => b.category === activeCategory);

  const displayed = filtered.slice(0, page * perPage);
  const hasMore = displayed.length < filtered.length;

  return (
    <section id="buyer-requests" className="py-16 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Global Buyers</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Buyer Request Board</h2>
            <p className="text-stone-500 text-sm mt-1.5">
              Global buyers post their purchasing needs for <span className="text-green-600 font-semibold">FREE</span>. Contact details visible to
              <span className="text-amber-600 font-semibold"> Paid Members</span> only.
            </p>
          </div>
          <button className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2">
            <i className="ri-add-circle-line"></i>
            Post Your Buying Need — FREE
          </button>
        </div>

        {/* Info Banner */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
              <i className="ri-price-tag-3-line text-green-600"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-green-800">Buyers Post for FREE</p>
              <p className="text-xs text-green-600">No cost to post your purchasing requirements</p>
            </div>
          </div>
          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
              <i className="ri-lock-unlock-line text-amber-600"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-amber-800">Sellers Need Paid Membership</p>
              <p className="text-xs text-amber-600">Upgrade to view buyer WhatsApp &amp; Email contacts</p>
            </div>
          </div>
          <div className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-full flex-shrink-0">
              <i className="ri-global-line text-stone-600"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-stone-800">20+ Active Requests</p>
              <p className="text-xs text-stone-500">From buyers in 20+ countries worldwide</p>
            </div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {buyerCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setPage(1); }}
              className={`text-xs font-medium px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-500 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {displayed.map((req, idx) => {
            const isLocked = idx >= 3;
            return (
              <div key={req.id} className="bg-white rounded-xl border border-stone-100 hover:border-amber-200 transition-all duration-200 overflow-hidden">
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Left: Buyer Info */}
                    <div className="flex items-center gap-3 md:w-48 flex-shrink-0">
                      <div className="relative">
                        <img
                          src={req.avatar}
                          alt={req.buyerName}
                          className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0"
                        />
                        {req.verified && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-green-500 rounded-full">
                            <i className="ri-check-line text-white text-xs" style={{ fontSize: "9px" }}></i>
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{req.buyerName}</p>
                        <p className="text-xs text-stone-500">{req.flag} {req.buyerCountry}</p>
                        <p className="text-xs text-stone-400 mt-0.5">{req.postedDate}</p>
                      </div>
                    </div>

                    {/* Middle: Request Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {req.urgent && (
                          <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-alarm-warning-line text-xs"></i>Urgent
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${categoryColors[req.category] || "bg-stone-100 text-stone-600"}`}>
                          {req.category}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm md:text-base mb-1.5">{req.title}</h3>
                      <p className="text-stone-500 text-xs leading-relaxed mb-3 line-clamp-2">{req.description}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {req.tags.map((tag) => (
                          <span key={tag} className="text-xs bg-stone-50 text-stone-600 px-2 py-0.5 rounded-full border border-stone-100">{tag}</span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-4 text-xs text-stone-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-box-3-line text-amber-500"></i>
                          <strong className="text-gray-800">{req.quantity}</strong>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-money-dollar-circle-line text-green-500"></i>
                          <strong className="text-gray-800">{req.budget}</strong>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-map-pin-line text-stone-400"></i>
                          {req.destination}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line text-stone-400"></i>
                          {req.deadline}
                        </span>
                      </div>
                    </div>

                    {/* Right: Contact (locked) */}
                    <div className="relative md:w-44 flex-shrink-0">
                      <div className={`space-y-2 ${isLocked ? "blur-sm select-none pointer-events-none" : ""}`}>
                        <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                          <i className="ri-whatsapp-line text-green-500 text-base"></i>
                          <span className="text-xs font-medium text-gray-700">+{req.id}** *** ****</span>
                        </div>
                        <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                          <i className="ri-mail-line text-amber-500 text-base"></i>
                          <span className="text-xs font-medium text-gray-700">buyer{req.id}@***.com</span>
                        </div>
                        <button className="w-full text-xs font-bold py-2 rounded-lg bg-amber-500 text-white cursor-pointer whitespace-nowrap">
                          Contact Buyer
                        </button>
                      </div>
                      {isLocked && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm rounded-lg">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full mb-1.5">
                            <i className="ri-lock-fill text-amber-500 text-sm"></i>
                          </div>
                          <p className="text-xs font-bold text-gray-800 mb-0.5 text-center">Members Only</p>
                          <button
                            onClick={() => setShowUpgrade(true)}
                            className="text-xs font-bold px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-1"
                          >
                            Unlock
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="text-center mt-6">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="px-8 py-3 border-2 border-amber-400 text-amber-600 font-semibold rounded-lg hover:bg-amber-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              Load More Requests
            </button>
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-bold text-xl md:text-2xl mb-1">Are You a Buyer?</h3>
            <p className="text-white/85 text-sm">Post your purchasing requirements for FREE and get contacted by verified global suppliers within 24 hours.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button className="px-6 py-3 bg-white text-amber-600 font-bold rounded-xl text-sm hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-circle-line mr-1.5"></i>Post FREE Request
            </button>
            <button
              onClick={() => setShowUpgrade(true)}
              className="px-6 py-3 bg-amber-700/40 hover:bg-amber-700/60 text-white font-bold rounded-xl text-sm border border-white/30 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vip-crown-fill mr-1.5"></i>Upgrade Membership
            </button>
          </div>
        </div>
      </div>

      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </section>
  );
}
